import React, { useEffect, useMemo, useState } from 'react';
import { memberInContextAtom } from '@src/global-state';
import { useAtomValue } from 'jotai';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { MemberInformation } from '@src/common/MemberInformation/MemberInformation';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { Button } from '@uhg-abyss/web/ui/Button';
import {
  Breadcumbs,
  ScreenConstants,
} from '@src/common/BreadCumbs/Breadcrumbs';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { BenefitSearchNoResultFound } from '@src/common/BenefitSearchNoResultsFound/BenefitSearchNoResultsFound';
import { API } from '@src/api';
import { BenefitSearchResult } from '@src/api.types';
import { Accordion } from '@uhg-abyss/web/ui/Accordion';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { LoadingSpinner } from '@uhg-abyss/web/ui/LoadingSpinner';
import { NoBenefitResultsModal } from '@src/common/BenefitSearchNoResultsFound/NoBenefitResultsModal';
import { BenefitDetailsResults } from './BenefitDetailsResults';
import { Popover } from '@uhg-abyss/web/ui/Popover';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { getFeatureFlagsFromStorage } from '@src/utills/storage';

export type Vendor = 'findhelp' | 'Unite Us';

export const BenefitSearchPage = () => {
  const memberInContext = useAtomValue(memberInContextAtom);
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const { navigate, getSearchParams } = useRouter();
  const [searchParams] = getSearchParams();
  const currentParams = Object.fromEntries([...searchParams]);
  const [benefitSearch, setBenefitSearch] =
    useState<BenefitSearchResult | null>(null);
  const [benefitterm, setbenefitterm] = useState<string>('');
  const [noSearchResult, setnoSearchResult] = useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [showApiErrorModal, setShowApiErrorModal] =
    React.useState<boolean>(false);
  const handleRespone = (res) => {
    if (res?.status !== 'success') {
      // to do add error modal for user
      console.error('Error fetching benefit plan');
      setIsLoading(false);
      setShowApiErrorModal(true);
    }
  };

  const featureFlagsFromStorage = getFeatureFlagsFromStorage();
  const isUniteUsApiEnabled: boolean = useMemo(() => {
    const flag: any = featureFlagsFromStorage['agent_uniteus_api'];

    if (!flag) {
      console.error(
        'Unite Us API feature flag did not conform to expected shape'
      );
      return false;
    }

    return flag['enabled'] ?? false;
  }, [featureFlagsFromStorage]);

  useEffect(() => {
    setbenefitterm(currentParams.benefitsearchterm);
  }, [currentParams.benefitsearchterm]);

  useEffect(() => {
    async function fetchBenefitSearch(searchterm: string) {
      setIsLoading(true);
      if (
        memberInContext?.benefitPlanPlatform !== undefined &&
        memberInContext?.benefitPlanID !== undefined &&
        memberInContext?.benefitPlanPlatform !== null &&
        memberInContext?.benefitPlanID !== null &&
        searchterm !== ''
      ) {
        const resp = await API.getBenefitSearchResults(
          memberInContext?.benefitPlanID,
          memberInContext?.benefitPlanPlatform,
          searchterm
        );

        handleRespone(resp);

        if (
          resp !== null &&
          resp !== undefined &&
          'status' in resp &&
          resp.status === 'success' &&
          'data' in resp
        ) {
          setShowApiErrorModal(false);
          setBenefitSearch(resp.data as BenefitSearchResult);
          setIsLoading(false);
          if (resp.data?.benefits.length == 0) {
            setnoSearchResult(true);
            setIsLoading(false);
          } else setnoSearchResult(false);
        } else {
          setBenefitSearch(null);
        }
        setIsLoading(false);
      }
    }
    fetchBenefitSearch(benefitterm);
  }, [benefitterm]);

  const form = useForm({
    defaultValues: {
      textForm: currentParams.benefitsearchterm,
    },
  });
  const onSubmit = (data) => {
    if (!data.textForm?.trim()) return;
    navigate(`/benefitsearchpage?benefitsearchterm=${data.textForm}`);
  };

  return (
    <React.Fragment>
      {isLoading ? (
        <div
          style={{
            zIndex: 999,
            backgroundColor: '#FFFFFF',
            opacity: 0.75,
            position: 'absolute',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LoadingSpinner
            size="xl"
            isLoading={isLoading}
            ariaLoadingLabel="Downloading files"
          />
        </div>
      ) : (
        ''
      )}
      <div>
        <div style={{ backgroundColor: '#F7FCFD' }} />
        <div
          style={{
            backgroundColor: '#EDF8FA',
            paddingRight: '5rem',
            paddingLeft: '5rem',
            paddingTop: '3rem',
            paddingBottom: '2rem',
          }}
        >
          <Grid>
            <Grid.Col span={{ xs: 12, md: 8, lg: 8 }}>
              <div>
                <Breadcumbs screen={ScreenConstants.BENEFIT_SEARCH_RESULTS} />
                <Heading serif data-cy="main-heading">
                  Find benefits for {memberInContext?.first_name || ''}
                </Heading>
                <div className="benefitsrchflexdisplay">
                  Type in your search here or browse benefits categories below
                  <Popover
                    content={
                      <div>
                        <strong>Benefits search tips</strong>
                        <br />
                        <br />
                        If you are not seeing results or not seeing the result
                        you expect, try modifying your search.
                        <br />
                        <strong>Prescription</strong> instead of Medication
                        <br />
                        <strong>Transportation</strong> instead of Transit
                        <br />
                        <strong>OTC</strong> (over-the-counter) to search for
                        Pharmacy benefits
                        <br />
                        <br />
                        If your search did not find benefits for addressing an
                        identified need, you may want to search by Value-added
                        services.
                        <br />
                        <strong>Value-added services</strong> can include a wide
                        variety of benefits such as meal service, fitness, home
                        and bath safety devices, diapers and more.
                      </div>
                    }
                    className="benefitsrchspacing"
                  >
                    <IconSymbol icon="question_exchange" />
                  </Popover>
                </div>
                <Grid>
                  <Grid.Col span={{ xs: 12, md: 12, lg: 6 }}>
                    <FormProvider state={form} onSubmit={onSubmit}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'flex-end',
                          gap: '1rem',
                        }}
                      >
                        <TextInput
                          placeholder="Select a Benefit"
                          key="benefitSearch-1"
                          model="textForm"
                          isClearable
                        />
                        <div style={{ flexShrink: 0 }}>
                          <Button
                            type="submit"
                            size="$md"
                            id="benefit_search"
                          >
                            Search
                          </Button>
                        </div>
                      </div>
                    </FormProvider>
                  </Grid.Col>
                </Grid>
              </div>
            </Grid.Col>
            <Grid.Col span={{ xs: 12, md: 4, lg: 4 }}>
              <MemberInformation
                name={`${memberInContext?.first_name} ${memberInContext?.last_name}`}
                memberId={memberInContext?.member_id || ''}
                dob={memberInContext?.DOB || ''}
                zipcode={memberInContext?.zipCode || ''}
                vendor={vendor}
                lob={memberInContext?.lineOfBusiness || ''}
                showAssessmentButton={
                  isUniteUsApiEnabled
                    ? !memberInContext?.medSup &&
                      sessionStorage.getItem('UHC-CC-Screening') === 'false'
                    : vendor !== 'Unite Us' &&
                      !memberInContext?.medSup &&
                      sessionStorage.getItem('UHC-CC-Screening') === 'false'
                }
              />
            </Grid.Col>
          </Grid>
        </div>
        {noSearchResult && <BenefitSearchNoResultFound />}
        {showApiErrorModal ? (
          <NoBenefitResultsModal />
        ) : (
          <div style={{ margin: '100px' }}>
            <Accordion type="multiple" isCollapsible>
              {benefitSearch?.benefits.map((item, index) => {
                return (
                  <Accordion.Item value={`sandbox-${index}`}>
                    <Accordion.Header headingLevel={1}>{item.name}</Accordion.Header>

                    <Accordion.Content>
                      <BenefitDetailsResults Id={item.id} />
                    </Accordion.Content>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};
